import { useEffect, useState } from "react";

/**
 * Whether the page is running as an installed app rather than in a browser tab.
 *
 * True inside the Android APK (a Trusted Web Activity reports `standalone`),
 * inside a Chrome-installed web app, and on iOS, where the media query is not
 * implemented and `navigator.standalone` carries the same answer.
 *
 * Evaluated once at mount and then followed, because a visitor can install the
 * app from the very page that is asking.
 */
export function useStandaloneDisplay(): boolean {
  const [standalone, setStandalone] = useState(() => isStandalone());

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const query = window.matchMedia("(display-mode: standalone)");
    const onChange = () => setStandalone(isStandalone());

    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return standalone;
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;

  const iosStandalone = (
    window.navigator as Navigator & { standalone?: boolean }
  ).standalone;
  if (iosStandalone) return true;

  return window.matchMedia?.("(display-mode: standalone)").matches ?? false;
}
